import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle, Warning, PaperPlaneTilt, FloppyDisk, CaretDown } from "@phosphor-icons/react";
import { INDIA_STATES, CITIES_BY_STATE, DEFAULT_STATE, DEFAULT_CITY } from "@/lib/indiaGeo";

const TYPES = [
  { v: "retail", l: "Retail / Shop" },
  { v: "grocery", l: "Grocery" },
  { v: "salon", l: "Salon / Beauty" },
  { v: "clinic", l: "Clinic / Doctor" },
  { v: "cafe", l: "Cafe / Restaurant" },
  { v: "services", l: "Services" },
  { v: "other", l: "Other" },
];

export default function Settings() {
  const { user, refresh } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [businessName, setBusinessName] = useState(user?.business_name || "");
  const [businessType, setBusinessType] = useState(user?.business_type || "retail");
  const [phone, setPhone] = useState(user?.phone || "");
  const [state, setState] = useState(user?.state || DEFAULT_STATE);
  const [city, setCity] = useState(user?.city || DEFAULT_CITY);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);

  const stateLocked = true; // matches launch policy
  const cityOptions = CITIES_BY_STATE[state] || [DEFAULT_CITY];

  const dirty =
    name.trim() !== (user?.name || "") ||
    businessName.trim() !== (user?.business_name || "") ||
    businessType !== (user?.business_type || "retail") ||
    (phone || "").trim() !== (user?.phone || "") ||
    state !== (user?.state || DEFAULT_STATE) ||
    city !== (user?.city || DEFAULT_CITY);

  const save = async () => {
    if (!dirty) return;
    setSaving(true);
    try {
      await api.put("/vendor/profile", {
        name: name.trim(),
        business_name: businessName.trim(),
        business_type: businessType,
        phone: phone.trim() || null,
        city,
        state,
      });
      await refresh();
      toast.success("Profile updated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    } finally { setSaving(false); }
  };

  const resendVerify = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/auth/resend-verification");
      toast[data.success ? "success" : "error"](data.message || "Sent");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send");
    } finally { setSending(false); }
  };

  return (
    <div data-testid="settings-page">
      {user?.is_admin && (
        <div className="mb-6 rounded-2xl p-4 sm:p-5 border border-brand-primary/30 bg-brand-primary/5" data-testid="admin-settings-note">
          <div className="font-heading font-bold">Admin account — profile is auto-managed</div>
          <div className="text-sm text-brand-text/75 mt-0.5">
            Regional/super admins don't need a vendor profile. Head to the Admin console to manage vendors and view economics.
          </div>
          <a href="/app/admin" className="mt-3 inline-flex items-center gap-2 bg-brand-primary hover:bg-brand-primaryHover text-white rounded-full px-4 py-2 text-sm font-semibold" data-testid="settings-to-admin-btn">Open Admin →</a>
        </div>
      )}
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.2em] text-brand-secondary mb-2">Account</div>
        <h1 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight">Settings</h1>
        <p className="text-brand-text/70 mt-1">Update your business details anytime.</p>
      </div>

      {/* Email verification card */}
      <div className={`mb-6 rounded-2xl p-5 border ${user?.email_verified ? "bg-brand-success/10 border-brand-success/30" : "bg-brand-warning/10 border-brand-warning/40"}`} data-testid="email-verification-card">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
          <div className="flex items-start gap-3">
            {user?.email_verified ? (
              <CheckCircle size={24} weight="duotone" className="text-brand-success shrink-0 mt-0.5" />
            ) : (
              <Warning size={24} weight="duotone" className="text-brand-warning shrink-0 mt-0.5" />
            )}
            <div>
              <div className="font-heading font-bold">
                {user?.email_verified ? "Email verified" : "Please verify your email"}
              </div>
              <div className="text-sm text-brand-text/75 mt-0.5">
                {user?.email_verified
                  ? `${user?.email} is confirmed. Password resets and important notifications will reach you.`
                  : `We sent a link to ${user?.email}. Click it to verify — needed for password resets & receipts.`}
              </div>
            </div>
          </div>
          {!user?.email_verified && (
            <button onClick={resendVerify} disabled={sending} data-testid="resend-verify-btn"
              className="inline-flex items-center gap-2 bg-brand-primary hover:bg-brand-primaryHover disabled:opacity-60 text-white rounded-full px-4 py-2 text-sm font-semibold shrink-0">
              <PaperPlaneTilt size={16} weight="duotone" /> {sending ? "Sending…" : "Resend email"}
            </button>
          )}
        </div>
      </div>

      <div className="bg-white border border-brand-border rounded-2xl p-6 sm:p-8 max-w-2xl">
        <h3 className="font-heading text-xl font-bold mb-6">Business profile</h3>
        <div className="grid sm:grid-cols-2 gap-5">
          <Field label="Business name">
            <input value={businessName} onChange={(e)=>setBusinessName(e.target.value)} data-testid="s-business-name"
              className="w-full h-11 px-4 rounded-xl bg-brand-bg border border-brand-border focus:border-brand-primary focus:ring-1 focus:ring-brand-primary outline-none" />
          </Field>
          <Field label="Business type">
            <div className="relative">
              <select value={businessType} onChange={(e)=>setBusinessType(e.target.value)} data-testid="s-business-type"
                className="w-full h-11 pl-4 pr-10 rounded-xl bg-brand-bg border border-brand-border appearance-none">
                {TYPES.map(t=><option key={t.v} value={t.v}>{t.l}</option>)}
              </select>
              <CaretDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-brand-textMuted pointer-events-none" />
            </div>
          </Field>
          <Field label="Owner name">
            <input value={name} onChange={(e)=>setName(e.target.value)} data-testid="s-name"
              className="w-full h-11 px-4 rounded-xl bg-brand-bg border border-brand-border focus:border-brand-primary focus:ring-1 focus:ring-brand-primary outline-none" />
          </Field>
          <Field label="Phone">
            <input value={phone} onChange={(e)=>setPhone(e.target.value)} data-testid="s-phone" placeholder="+91 98765 43210"
              className="w-full h-11 px-4 rounded-xl bg-brand-bg border border-brand-border focus:border-brand-primary focus:ring-1 focus:ring-brand-primary outline-none" />
          </Field>
          <Field label="State">
            <div className="relative">
              <select
                value={state}
                onChange={(e)=> {
                  const v = e.target.value;
                  const cities = CITIES_BY_STATE[v] || [];
                  setState(v);
                  setCity(cities[0] || DEFAULT_CITY);
                }}
                disabled={stateLocked}
                data-testid="s-state"
                className="w-full h-11 pl-4 pr-10 rounded-xl bg-brand-bg border border-brand-border appearance-none disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {INDIA_STATES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              <CaretDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-brand-textMuted pointer-events-none" />
            </div>
            {stateLocked && <p className="mt-1 text-[11px] text-brand-textMuted">Locked to Uttar Pradesh for launch</p>}
          </Field>
          <Field label="City">
            <div className="relative">
              <select value={city} onChange={(e)=>setCity(e.target.value)} data-testid="s-city"
                className="w-full h-11 pl-4 pr-10 rounded-xl bg-brand-bg border border-brand-border appearance-none">
                {cityOptions.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              <CaretDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-brand-textMuted pointer-events-none" />
            </div>
          </Field>
          <Field label="Email (read only)">
            <input value={user?.email || ""} disabled data-testid="s-email"
              className="w-full h-11 px-4 rounded-xl bg-brand-bg border border-brand-border text-brand-textMuted cursor-not-allowed" />
          </Field>
          <Field label="Public slug (read only)">
            <input value={user?.slug || ""} disabled data-testid="s-slug"
              className="w-full h-11 px-4 rounded-xl bg-brand-bg border border-brand-border text-brand-textMuted cursor-not-allowed font-mono text-sm" />
          </Field>
        </div>
        <div className="mt-8 flex gap-3">
          <button onClick={save} disabled={!dirty || saving} data-testid="save-profile"
            className="inline-flex items-center gap-2 bg-brand-primary hover:bg-brand-primaryHover disabled:bg-brand-border disabled:text-brand-textMuted text-white rounded-full px-6 py-3 text-sm font-semibold">
            <FloppyDisk size={18} weight="duotone" /> {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-[0.2em] text-brand-textMuted">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}
